/**
 * Subscription mutations: freeze, unfreeze, cancel. Used by useSubscriptionActions.
 */
import { apiPost } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { unwrapEnvelope } from '../../query/normalizers/unwrapEnvelope';
import { getDateServiceConfig } from '../config';
import { filterFreezeToggleableDates } from './helpers';

export interface FreezeUnfreezeParams {
  customer_id: number;
  customer_subscription_id: number;
  dates: string[];
}

export interface CancelParams {
  customer_id: number;
  customer_subscription_id: number;
}

async function postFreezeUnfreeze(mode: 'freeze' | 'unfreeze', params: FreezeUnfreezeParams): Promise<unknown> {
  const { menu_selection_before } = getDateServiceConfig();
  const dates = filterFreezeToggleableDates(params.dates ?? [], menu_selection_before);
  if (dates.length === 0) {
    throw new Error(
      mode === 'freeze'
        ? 'No freeze dates available in the unlocked window'
        : 'No unfreeze dates available in the unlocked window',
    );
  }
  const endpoint = mode === 'freeze' ? endpoints.subscriptionFreeze : endpoints.subscriptionUnFreeze;
  const raw = await apiPost<unknown>(endpoint, {
    customer_id: params.customer_id,
    customer_subscription_id: params.customer_subscription_id,
    dates,
  });
  return unwrapEnvelope(
    raw,
    mode === 'freeze' ? 'Could not freeze selected dates' : 'Could not unfreeze selected dates',
  );
}

export async function freezeSubscription(params: FreezeUnfreezeParams): Promise<unknown> {
  return postFreezeUnfreeze('freeze', params);
}

export async function unfreezeSubscription(params: FreezeUnfreezeParams): Promise<unknown> {
  return postFreezeUnfreeze('unfreeze', params);
}

export async function cancelSubscription(params: CancelParams): Promise<unknown> {
  const raw = await apiPost<unknown>(endpoints.subscriptionCancel, {
    customer_id: params.customer_id,
    customer_subscription_id: params.customer_subscription_id,
  });
  return unwrapEnvelope(raw, 'Could not cancel subscription');
}
